import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import type { SceneStore } from '@os/doc';
import { worldToScreen } from '@os/engine';
import type { EditorController } from './controller';
import { CANVAS_UI } from './canvas-ui';
import { authorIds, authorsOf, type AuthorsSource } from './authors';
import { graphOf } from './graph-of';
import { useAuthorNames } from './useAuthors';

/**
 * Who added the selection, in a small tag under its box (P5d, backlog §10.5): the author's colour and name, "You",
 * "{n} authors"… Hidden while a pointer is down (a gesture) and on boards without authors (local mode).
 */
export function AuthorTag({ controller, store, source }: { controller: EditorController; store: SceneStore; source: AuthorsSource }) {
  const { t, i18n } = useTranslation();
  const ui = useSyncExternalStore(controller.subscribeUi, controller.ui);
  const camera = useSyncExternalStore(controller.subscribeCamera, () => controller.camera);
  const subscribeList = useCallback((cb: () => void) => store.subscribeList(cb), [store]);
  const list = useSyncExternalStore(subscribeList, () => store.list());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const down = () => setBusy(true);
    const up = () => setBusy(false);
    window.addEventListener('pointerdown', down, true);
    window.addEventListener('pointerup', up, true);
    window.addEventListener('pointercancel', up, true);
    return () => {
      window.removeEventListener('pointerdown', down, true);
      window.removeEventListener('pointerup', up, true);
      window.removeEventListener('pointercancel', up, true);
    };
  }, []);
  const selected = new Set(ui.selection);
  const objects = list.filter((o) => selected.has(o.id));
  const names = useAuthorNames(source, authorIds(objects));
  const view = authorsOf(objects, { me: source.me, names, t, lng: i18n.language });
  const g = graphOf(store, list);
  const boxes = objects.flatMap((o) => (g.world.get(o.id) ? [g.world.get(o.id)!] : []));
  if (!view || !boxes.length) return null;
  const at = worldToScreen(camera, { x: Math.min(...boxes.map((b) => b.x)), y: Math.max(...boxes.map((b) => b.y + b.h)) });
  return (
    <div
      data-author-tag=""
      {...{ [CANVAS_UI]: '' }}
      hidden={busy}
      style={{ left: `${Math.round(at.x)}px`, top: `${Math.round(at.y + 12)}px` }}
      className="pointer-events-none absolute z-[6] flex max-w-60 items-center gap-1.5 rounded-full bg-surface px-2 py-0.5 font-sans text-xs text-fg shadow-sm"
    >
      <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-fg-subtle" style={view.color ? { backgroundColor: view.color } : undefined} />
      <span className="truncate">{view.label}</span>
    </div>
  );
}
